import { PropsWithChildren } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { useToast } from '@givebutter/primitives/toast';
import { updateReportSchedule } from 'api/reports';
import { useDialogRoute } from 'components/dialog-route/DialogRouteContext';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentReport } from 'customHooks/useCurrentReport';
import { accountQueryKeys } from 'queries/account-query-keys';
import { GivebutterAxiosError } from 'types/error';

interface ResumeReportScheduleDialogProps extends PropsWithChildren {
    scheduleId: number;
}

const ResumeReportScheduleDialog = (props: ResumeReportScheduleDialogProps) => {
    const { children, scheduleId } = props;
    const { goBack } = useDialogRoute();
    const queryClient = useQueryClient();
    const { accountId } = useCurrentAccount();
    const { data: report } = useCurrentReport();
    const { success, error } = useToast();

    const { mutate: updateSchedule } = useMutation({
        mutationFn: () =>
            updateReportSchedule(accountId, report.id, scheduleId, { status: 'published' }),
        onError: (e: GivebutterAxiosError) => {
            error(e.response?.data.message ?? 'Failed to resume recurring report');
        },
        onSuccess: () => {
            success('Resumed recurring shared report successfully');
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.reportSchedulesWithFilters(accountId, report.id),
                exact: false,
            });
            goBack();
        },
    });

    return (
        <AlertDialog.Root type="warning">
            {children}
            <AlertDialog.Portal>
                <AlertDialog.Overlay />
                <AlertDialog.Content>
                    <AlertDialog.Header>
                        <AlertDialog.Icon />
                        <AlertDialog.Close />
                    </AlertDialog.Header>
                    <AlertDialog.Body>
                        <AlertDialog.Title>Resume sharing report?</AlertDialog.Title>
                        <AlertDialog.Description>
                            Resuming will restart sharing this report with any attached recipients
                            at it's set schedule.
                        </AlertDialog.Description>
                    </AlertDialog.Body>
                    <AlertDialog.Footer>
                        <AlertDialog.Cancel>Cancel</AlertDialog.Cancel>
                        <AlertDialog.Action onClick={() => updateSchedule()}>
                            Resume sharing
                        </AlertDialog.Action>
                    </AlertDialog.Footer>
                </AlertDialog.Content>
            </AlertDialog.Portal>
        </AlertDialog.Root>
    );
};

const ResumeReportScheduleDialogTrigger = AlertDialog.Trigger;

const Root = ResumeReportScheduleDialog;
const Trigger = ResumeReportScheduleDialogTrigger;

export {
    ResumeReportScheduleDialog,
    ResumeReportScheduleDialogTrigger,
    //
    Root,
    Trigger,
};
